import { ReactNode, memo, useEffect, useRef, useState } from 'react';
// import Dialog from 'presentations/views/components/atoms/Dialog';
import translations from './i18n.json';
import { translateInComponent } from 'infras/i18n';
import styled from '@emotion/styled';
import {
  borderRadius,
  fontSize,
  fontWeight,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import {
  gray01,
  gray02,
  primary,
  white,
} from 'presentations/views/styles/color';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import StepperProgress from 'presentations/views/components/atoms/StepperProgress';
import Popper from 'presentations/views/components/atoms/Popper';

export interface GuideItem {
  querySelector?: string;
  title?: string;
  content: string | ReactNode;
}

interface Props {
  isOpen: boolean;
  guideItems: GuideItem[];
  onClose: () => void;
}

const Component = memo((props: Props) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [title, setTitle] = useState<string | null>(null);
  const [content, setContent] = useState<string | ReactNode | null>(null);
  const [targetEl, setTargetEl] = useState<Element | null>(null);
  const [targetTop, setTargetTop] = useState<number>(0);
  const [targetLeft, setTargetLeft] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);
  const [targetWidth, setTargetWidth] = useState<number>(0);

  const onNext = () => {
    const index = activeIndex + 1;
    if (index >= props.guideItems.length) {
      props.onClose();
      return;
    }
    setActiveIndex(index);
  };

  const onPrev = () => {
    const index = activeIndex - 1;
    if (index < 0) return;
    setActiveIndex(index);
  };

  const t = translateInComponent(translations);

  useEffect(() => {
    if (activeIndex < 0 || activeIndex >= props.guideItems.length) return;
    const activeGuide = props.guideItems[activeIndex];
    if (!activeGuide.querySelector) return;
    const el = document.querySelector(activeGuide.querySelector);
    setTargetEl(el || null);
    setTitle(activeGuide.title || null);
    setContent(activeGuide.content);
  }, [activeIndex, props.guideItems]);

  useEffect(() => {
    if (!targetEl) return;
    const rect = targetEl.getBoundingClientRect();
    setTargetTop(rect.top);
    setTargetLeft(rect.left);
    setTargetWidth(rect.width);
    setTargetHeight(rect.height);
  }, [targetEl]);

  const isFirstIndex = activeIndex === 0;
  const isLastIndex = props.guideItems.length - 1 === activeIndex;

  if (!props.isOpen || !targetEl) {
    return null;
  }

  return (
    <>
      <Backdrop
        top={targetTop}
        left={targetLeft}
        width={targetWidth}
        height={targetHeight}
      />

      <Popper isOpen anchor={targetEl}>
        <Container>
          <Header>
            <StepperProgress
              steps={props.guideItems.length}
              activeStep={activeIndex + 1}
              width={60}
              isHideLabel
            />
            <CloseIconButton onClick={props.onClose}>
              <CloseIcon name="close" />
            </CloseIconButton>
          </Header>
          <Content>
            {title && <Title>{title}</Title>}
            {content}
          </Content>
          <Footer>
            <ButtonContainer>
              {!isFirstIndex && <Button onClick={onPrev}>{t('prev')}</Button>}
            </ButtonContainer>
            <IndicatorContainer>
              {activeIndex + 1}/{props.guideItems.length}
            </IndicatorContainer>
            <ButtonContainer>
              <Button color="primary" onClick={onNext} variant="contained">
                {t(isLastIndex ? 'close' : 'next')}
              </Button>
            </ButtonContainer>
          </Footer>
        </Container>
      </Popper>
    </>
  );
});

const Backdrop = styled.div<{
  top: number;
  left: number;
  width: number;
  height: number;
}>`
  box-shadow: rgba(31, 116, 242, 0.8) 0px 0px 8px 3px,
    rgba(20, 20, 20, 0.5) 0px 0px 0px 1000vh;
  position: absolute;
  border: 2px solid ${primary};
  border-radius: ${borderRadius(8)};
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  z-index: ${zIndex(1500)};
`;

const Content = styled.div`
  padding: 0 ${spacing(16)};
  flex: 1;
`;

const Container = styled.div`
  background-color: ${white};
  border-radius: ${spacing(8)};
  width: 300px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 ${spacing(24)};
  box-shadow: 0px 8px 20px 0px rgba(255, 255, 255, 0.25);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing(12)} ${spacing(16)};
`;
const Title = styled.div`
  display: flex;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  margin-bottom: ${spacing(8)};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing(12)};
  padding: ${spacing(8)} ${spacing(16)};
`;
const ButtonContainer = styled.div`
  display: flex;
`;
const IndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${gray01};
  font-size: ${fontSize(13)};
`;
const CloseIconButton = styled(IconButton)`
  padding: ${spacing(4)};
`;
const CloseIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${gray02};
`;

export default Component;
